// DesignSystemView.tsx — third tab of /content-map ("The Brand Book").
//
// DIVISION OF TRUTH (Erez, 2026-06-12):
//   • skill://design-system (Neon `skills`, slug `design-system`) = the WRITTEN
//     law — every rule, number and gate lives THERE and only there.
//   • THIS tab = the VISUAL expression of that law — swatches, rendered type,
//     a live specimen, and the calibration set. No rule prose is duplicated
//     here; if this tab and the skill ever disagree, the skill wins.
//
// The calibration set is fetched LIVE from /api/content-map (the canonical
// post-1 slides) so this page can never go stale against production.

import { useEffect, useState } from "react";
import { useApi } from "@/lib/api";
import { architectureT as T } from "../blueprint/ArchitectureBlocks";

// ── The law tokens (visual values — the numbers live in the skill) ──────────

const LAW = {
  bone: "#F3EFE2",
  ink: "#1C2021",
  petrol: "#2B5560",
  petrolTint: "#B3CAD0",
  retired: "#B85638",
};

const FACE = '"Inter Tight","Inter",system-ui,-apple-system,sans-serif';
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

// ── Swatches ─────────────────────────────────────────────────────────────────

type Swatch = {
  name: string;
  hex: string;
  role: string;
  on: "bone" | "ink";
  ratioOnBone?: string;
  pass?: boolean;
  retired?: boolean;
};

const SWATCHES: Swatch[] = [
  {
    name: "Bone",
    hex: LAW.bone,
    role: "The surface. Exact, everywhere — including inside the art.",
    on: "bone",
  },
  {
    name: "Ink",
    hex: LAW.ink,
    role: "The only text color.",
    on: "ink",
    ratioOnBone: "13.3:1",
    pass: true,
  },
  {
    name: "Petrol · deep",
    hex: LAW.petrol,
    role: "The eyebrow + the one emphasis fill.",
    on: "ink",
    ratioOnBone: "6.6:1",
    pass: true,
  },
  {
    name: "Petrol · tint",
    hex: LAW.petrolTint,
    role: "The uniformity wash across a list of equals. Fill only, never text.",
    on: "bone",
  },
  {
    name: "Terracotta",
    hex: LAW.retired,
    role: "Retired 2026-06-12. Never appears in a published post.",
    on: "ink",
    ratioOnBone: "3.8:1",
    pass: false,
    retired: true,
  },
];

// ── Small components ─────────────────────────────────────────────────────────

function SectionHeading({
  eyebrow,
  title,
  blurb,
}: {
  eyebrow?: string;
  title: string;
  blurb?: string;
}) {
  return (
    <div style={{ margin: "0 0 14px" }}>
      {eyebrow && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: T.skyDark,
            marginBottom: 6,
          }}
        >
          {eyebrow}
        </div>
      )}
      <h2
        style={{
          fontSize: 22,
          fontWeight: 800,
          color: T.ink,
          margin: "0 0 8px",
          letterSpacing: "-0.015em",
        }}
      >
        {title}
      </h2>
      {blurb && (
        <p style={{ fontSize: 13, color: T.ink2, lineHeight: 1.6, margin: 0, maxWidth: 760 }}>
          {blurb}
        </p>
      )}
    </div>
  );
}

function ContrastBadge({ ratio, pass }: { ratio: string; pass: boolean }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 10.5,
        fontWeight: 700,
        fontFamily: MONO,
        color: pass ? "#1B7A4B" : "#B23A2E",
        background: pass ? "rgba(27,122,75,0.10)" : "rgba(178,58,46,0.10)",
        border: `1px solid ${pass ? "rgba(27,122,75,0.30)" : "rgba(178,58,46,0.30)"}`,
        borderRadius: 999,
        padding: "1px 8px",
        letterSpacing: "0.02em",
      }}
    >
      {ratio} {pass ? "PASS" : "FAIL"}
    </span>
  );
}

function SwatchTile({ s }: { s: Swatch }) {
  return (
    <div
      style={{
        background: T.white,
        border: `1px solid ${T.line}`,
        borderRadius: 10,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        opacity: s.retired ? 0.92 : 1,
      }}
    >
      <div
        style={{
          background: s.hex,
          aspectRatio: "4 / 3",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          padding: "12px 14px",
        }}
      >
        <span
          style={{
            fontFamily: MONO,
            fontSize: 11.5,
            fontWeight: 700,
            color: s.on === "ink" ? LAW.bone : LAW.ink,
            letterSpacing: "0.04em",
          }}
        >
          {s.hex.toUpperCase()}
        </span>
        {s.retired && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: LAW.bone,
              background: "rgba(28,32,33,0.55)",
              borderRadius: 6,
              padding: "2px 7px",
            }}
          >
            Retired
          </span>
        )}
      </div>
      <div style={{ padding: "10px 14px 14px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 800, color: T.ink }}>{s.name}</div>
          {s.ratioOnBone && <ContrastBadge ratio={s.ratioOnBone} pass={!!s.pass} />}
        </div>
        <p style={{ fontSize: 12, color: T.ink2, margin: "6px 0 0", lineHeight: 1.5 }}>
          {s.role}
        </p>
      </div>
    </div>
  );
}

// The live specimen — a slide rendered IN the law: bone surface, Inter Tight,
// petrol eyebrow caps, ink title with no trailing period, one petrol emphasis
// fill among hairline equals, the italic anchor. Zero terracotta.
function LawSpecimen() {
  return (
    <div
      style={{
        background: LAW.bone,
        borderRadius: 12,
        border: `1px solid ${T.line}`,
        padding: "44px 52px 40px",
        overflow: "hidden",
        fontFamily: FACE,
        aspectRatio: "1 / 1",
        maxWidth: 520,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.135em",
          textTransform: "uppercase",
          color: LAW.petrol,
          marginBottom: 22,
        }}
      >
        AI Operations
      </div>
      <h3
        style={{
          fontFamily: FACE,
          fontSize: 44,
          fontWeight: 400,
          color: LAW.ink,
          margin: "0 0 28px",
          lineHeight: 1.06,
          letterSpacing: "-0.015em",
        }}
      >
        First get the data together
      </h3>
      <div style={{ display: "flex", alignItems: "center", gap: 16, margin: "4px 0 auto" }} aria-hidden>
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              width: 52,
              height: 52,
              borderRadius: 10,
              border: `2px solid ${LAW.ink}`,
              background: i === 1 ? LAW.petrol : "transparent",
            }}
          />
        ))}
      </div>
      <p
        style={{
          fontFamily: FACE,
          fontStyle: "italic",
          fontSize: 17,
          fontWeight: 400,
          color: LAW.ink,
          margin: "28px 0 0",
          lineHeight: 1.4,
        }}
      >
        Then put agents on it to do the work.
      </p>
    </div>
  );
}

// ── Type scale — RENDERED, not described ─────────────────────────────────────

function TypeRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 24,
        padding: "18px 24px",
        borderBottom: `1px solid ${T.line}`,
      }}
    >
      <div
        style={{
          fontFamily: MONO,
          fontSize: 10.5,
          color: T.ink3,
          width: 210,
          flexShrink: 0,
          letterSpacing: "0.02em",
        }}
      >
        {label}
      </div>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

function TypeScaleSpecimens() {
  return (
    <div
      style={{
        background: LAW.bone,
        border: `1px solid ${T.line}`,
        borderRadius: 12,
        overflow: "hidden",
        fontFamily: FACE,
        marginBottom: 44,
      }}
    >
      <TypeRow label="EYEBROW · 700 · 26px@1024 · .135em">
        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.135em",
            textTransform: "uppercase",
            color: LAW.petrol,
          }}
        >
          AI Operations
        </span>
      </TypeRow>
      <TypeRow label="TITLE · ≤400 · 65–68px band · no period">
        <span style={{ fontSize: 34, fontWeight: 400, color: LAW.ink, letterSpacing: "-0.015em", lineHeight: 1.1 }}>
          What the brain does
        </span>
      </TypeRow>
      <TypeRow label="BODY · 32–40px band · ≥28px floor">
        <span style={{ fontSize: 17, fontWeight: 400, color: LAW.ink }}>
          Mail — every promise made
        </span>
      </TypeRow>
      <TypeRow label="ANCHOR · italic · 28–32px band">
        <span style={{ fontSize: 15, fontStyle: "italic", color: LAW.ink }}>
          Memory becomes context. Context becomes action.
        </span>
      </TypeRow>
    </div>
  );
}

// ── The calibration set — fetched live, never stale ──────────────────────────

type ContentItem = { title?: string; image_urls?: string[] };

function CalibrationSet() {
  const api = useApi();
  const [slides, setSlides] = useState<string[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api("/api/content-map");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as { items?: ContentItem[] };
        const canonical = (json.items ?? []).find(
          (i) => i.title === "The anatomy of an AI Operations System",
        );
        if (!cancelled) {
          if (canonical?.image_urls?.length) setSlides(canonical.image_urls);
          else setFailed(true);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (failed) {
    return (
      <p style={{ fontSize: 12.5, color: T.ink3 }}>
        Calibration set unavailable — the canonical post could not be loaded.
      </p>
    );
  }
  if (!slides) {
    return <p style={{ fontSize: 12.5, color: T.ink3 }}>Loading the calibration set…</p>;
  }
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
        gap: 12,
      }}
    >
      {slides.map((src, i) => (
        <div
          key={src}
          style={{
            border: `1px solid ${T.line}`,
            borderRadius: 10,
            overflow: "hidden",
            background: LAW.bone,
          }}
        >
          <img
            src={src}
            alt={`Calibration slide ${i + 1}`}
            style={{ display: "block", width: "100%", aspectRatio: "1 / 1", objectFit: "cover" }}
            loading="lazy"
          />
          <div
            style={{
              fontFamily: MONO,
              fontSize: 10,
              color: T.ink3,
              padding: "6px 10px",
              background: T.white,
              borderTop: `1px solid ${T.line}`,
            }}
          >
            SLIDE {i + 1}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function DesignSystemView() {
  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: T.skyDark,
            marginBottom: 8,
          }}
        >
          MyJarvis · Marketing · law locked 2026-06-12
        </div>
        <h1
          style={{
            fontSize: 30,
            fontWeight: 800,
            color: T.ink,
            margin: 0,
            letterSpacing: "-0.02em",
          }}
        >
          The Brand Book
        </h1>
        <p style={{ fontSize: 14, color: T.ink2, maxWidth: 780, lineHeight: 1.6 }}>
          The visual expression of the design law. Every rule and number lives
          in <strong>skill://design-system</strong>; this page shows what they
          look like. If the two ever disagree, the skill wins.
        </p>
      </div>

      <SectionHeading
        eyebrow="The first look"
        title="What every MyJarvis slide reads as"
      />
      <div style={{ marginBottom: 44 }}>
        <LawSpecimen />
      </div>

      <SectionHeading
        eyebrow="Color"
        title="Four colors carry meaning"
        blurb="Bone is the surface, Ink is the only text color, Petrol is the accent family — deep for emphasis, tint for uniformity. Text pairs must clear 4.5:1; terracotta does not, which is why it left."
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
          gap: 14,
          marginBottom: 44,
        }}
      >
        {SWATCHES.map((s) => (
          <SwatchTile key={s.name} s={s} />
        ))}
      </div>

      <SectionHeading
        eyebrow="Type"
        title="One face, rendered at the law"
        blurb="Inter Tight everywhere — ≤400 for display and body, the eyebrow at 700 by exception, the italic anchor as the only departure."
      />
      <TypeScaleSpecimens />

      <SectionHeading
        eyebrow="The calibration set"
        title="The reference the law measures against"
        blurb="The canonical post, pulled live from the wall. Eyebrow, title, art style and density are measured from these five slides."
      />
      <div style={{ marginBottom: 24 }}>
        <CalibrationSet />
      </div>

      <div
        style={{
          fontSize: 11.5,
          color: T.ink3,
          lineHeight: 1.6,
          maxWidth: 760,
          paddingTop: 8,
        }}
      >
        Written law: skill://design-system — rules, numbers, gates and the
        review protocol live there, not here.
      </div>
    </div>
  );
}
